import React, { Fragment, useState, useEffect, useRef } from 'react';
import qs from 'qs';
import { useRefinementList } from 'react-instantsearch';
import RefinementTag from './RefinementTag';
import { CheckboxFacetUI } from './CheckboxFacetUI';
import { getCollectionHandleFromPath, updateSearchRefFromUrl } from './utils';
import { trackStoreFilter } from '@jbhi-fi/utils/src/ga4Tracking';
import { pushToDataLayer } from '@jbhi-fi/utils/src/ga4Tracking/datalayer';
import { postAlgoliaFilterClick, postAlgoliaFilterView } from '../../../utils/algolia/search-insights';
import { AlgoliaApiClient } from '../../../api/localisation/AlgoliaApiClient';
import { useQuery } from 'react-query';
import { AlgoliaStoreData } from '@jbhi-fi/apis/src/algolia/types/AlgoliaStoreData';
import type { TrackingInfo } from '../../../types/search';

interface CheckboxRefinementProps {
  facet: string;
  limit: number;
  showMoreLimit: number;
  setDisableScroll: (v: boolean) => void;
  trackingInfo: TrackingInfo;
  onDropdownToggle?: (isOpen: boolean) => void;
  isSearchPage?: boolean;
}

type RefinementListSortBy = 'count:asc' | 'count:desc' | 'name:asc' | 'name:desc';

type CustomRefinementListSortBy = RefinementListSortBy | 'numeric:asc' | 'numeric:desc';

type MakeCheckboxItemsParams = {
  items: Array<any>;
  filterSearchTerm: string;
  sortOrder: CustomRefinementListSortBy;
  facet: string;
  algoliaStoreData: Array<AlgoliaStoreData> | undefined;
};

/**
 * Facet Sorting Config
 * Map so we can retrieve the sort order using the facet name directly
 */
const facetSortingConfig: Map<string, CustomRefinementListSortBy> = new Map([
  ...(window.Jbhifi.facetSorting?.countAscending?.map((item: string) => [item.trim(), 'count:asc']) || []),
  ...(window.Jbhifi.facetSorting?.nameAscending?.map((item: string) => [item.trim(), 'name:asc']) || []),
  ...(window.Jbhifi.facetSorting?.nameDescending?.map((item: string) => [item.trim(), 'name:desc']) || []),
  ...(window.Jbhifi.facetSorting?.numericAscending?.map((item: string) => [item.trim(), 'numeric:asc']) || []),
  ...(window.Jbhifi.facetSorting?.numericDescending?.map((item: string) => [item.trim(), 'numeric:desc']) || []),
]);

const ON_DISPLAY_FACET = 'Display Models In-Store';

/**
 * Function for to create checkboxes based on the items and facets
 */
const makeCheckboxItems = ({
  items,
  filterSearchTerm,
  sortOrder,
  facet,
  algoliaStoreData,
}: MakeCheckboxItemsParams) => {
  const normalizedFilter = filterSearchTerm.trim().toLowerCase();
  const filteredItems =
    facet === ON_DISPLAY_FACET && algoliaStoreData?.length
      ? items.filter(item =>
          algoliaStoreData.some(
            store =>
              store.storeName &&
              store.storeName.toLowerCase().includes(normalizedFilter) &&
              store.objectID === item.label
          )
        )
      : items.filter(s => s.label.toLowerCase().startsWith(normalizedFilter));
  const checkboxItems = filterSearchTerm ? filteredItems : items;

  if (!sortOrder.startsWith('numeric')) {
    return checkboxItems;
  }

  // Custom numerical sort using label
  return checkboxItems.sort((a, b) => {
    const refinedDiff = b.isRefined - a.isRefined; // Refined items first
    if (refinedDiff !== 0) return refinedDiff;

    const aValue = parseFloat(a.label);
    if (isNaN(aValue)) return 1;

    const bValue = parseFloat(b.label);
    if (isNaN(bValue)) return -1;

    if (sortOrder === 'numeric:asc') {
      return aValue - bValue;
    }

    return bValue - aValue;
  });
};

export const CheckboxRefinement = ({
  facet,
  limit,
  showMoreLimit,
  setDisableScroll,
  trackingInfo,
  onDropdownToggle,
  isSearchPage,
}: CheckboxRefinementProps) => {
  const attribute = `facets.${facet}`;
  const sortOrder = facetSortingConfig.get(facet.toLowerCase()) || 'count:desc';

  const { items, refine } = useRefinementList({
    attribute,
    showMoreLimit,
    limit,
    sortBy: ['isRefined:desc', (sortOrder.startsWith('numeric') ? 'count:desc' : sortOrder) as RefinementListSortBy],
  });

  const [showCheckboxInput, setShowCheckboxInput] = useState(false);
  const RefineButton = useRef<HTMLButtonElement>(null);
  const RefineDropdown = useRef<HTMLDivElement>(null);
  const [checkedItems, setCheckedItems] = useState(items.filter(i => i.isRefined));
  const [filterSearchTerm, setFilterSearchTerm] = useState('');
  const clickTypeRef = useRef<'checkbox' | 'text'>('checkbox');

  const searchState = qs.parse(window.location.search.slice(1)) as any;
  const query = typeof searchState?.query === 'string' ? searchState.query : '';
  const searchType = isSearchPage ? 'site search' : 'collection';
  const searchTerm = isSearchPage ? query : getCollectionHandleFromPath(window.location.pathname);
  const searchRef = useRef(searchState);
  const { data: storesData } = useQuery({
    queryKey: ['storesByIds', items.map(item => item.label)],
    queryFn: () => AlgoliaApiClient.getStoresByIds(items.map(item => item.label)),
    enabled: facet === ON_DISPLAY_FACET && items.length > 0,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
  const checkboxItems = makeCheckboxItems({ items, filterSearchTerm, sortOrder, facet, algoliaStoreData: storesData });
  const isSomeRefined = items.filter(f => f.isRefined === true).length;

  let prependedLabel = '';

  useEffect(() => {
    // Add Inches to screen size facet
    if (facet === 'Screen Size (Inches)') {
      prependedLabel = '"';
    }
    return () => {
      document.body.removeEventListener('pointerdown', externalClick);
    };
  }, []);

  useEffect(() => {
    const updatedItems = makeCheckboxItems({ items, filterSearchTerm, sortOrder, facet, algoliaStoreData: storesData });
    if (checkedItems.length === 0 && updatedItems.filter(i => i.isRefined).length > 0) {
      setCheckedItems(updatedItems.filter(i => i.isRefined));
    } else if (updatedItems.filter(i => i.isRefined).length === 0) {
      setCheckedItems([]);
    }
  }, [items, filterSearchTerm]);

  /**
   * Function to handle dropdown toggle (clicking button)
   */
  const toggleDropdown = event => {
    onDropdownToggle?.(!showCheckboxInput);
    pushToDataLayer({
      event: 'open_filter_group',
      filter_group: facet,
      search_type: searchType,
      search_term: searchTerm,
    });
    postAlgoliaFilterView(facet);
    event.preventDefault();
    setFilterSearchTerm('');
    setShowCheckboxInput(!showCheckboxInput);
  };

  /**
   * Function to handle external clicks
   */
  const externalClick = event => {
    const Button = RefineButton.current;
    const Dropdown = RefineDropdown.current;

    // Close price range dropdown if clicked outside of target window (desktop)
    if (!event || (Dropdown && !Dropdown.contains(event.target) && Button && !Button.contains(event.target))) {
      document.body.removeEventListener('pointerdown', externalClick);
      setShowCheckboxInput(false);
      setDisableScroll(false);
    }
  };

  useEffect(() => {
    if (showCheckboxInput) {
      document.body.addEventListener('pointerdown', externalClick);
      setDisableScroll(true);
    } else {
      document.body.removeEventListener('pointerdown', externalClick);
      setDisableScroll(false);
    }
    return () => {
      document.body.removeEventListener('pointerdown', externalClick);
    };
  }, [showCheckboxInput]);

  const labelOnClickHandler = () => {
    clickTypeRef.current = 'text';
  };

  const toggleCheck = (e, item) => {
    const filterType = clickTypeRef.current;
    clickTypeRef.current = 'checkbox';
    const isOnDisplayFacet = facet === ON_DISPLAY_FACET;
    const isSelecting = !item.isRefined;

    if (!isOnDisplayFacet) {
      trackStoreFilter({
        isSelected: isSelecting,
        filterValue: item.label,
        filterGroup: facet,
        filterName: 'undefined',
        filterType,
        searchType,
        searchTerm,
      });
    }
    postAlgoliaFilterClick(facet, item.label);
    e.persist();
    let values = '';

    const filters = Object.keys(searchState)
      .filter(key => !['query', 'page', 'hitsPerPage', 'sortBy'].includes(key))
      .reduce((obj: any, key: string) => {
        if (key.toLowerCase() === 'price') {
          obj[key.toLowerCase()] = { type: 'range', value: searchState[key] };
        } else {
          obj[key.toLowerCase()] = Array.isArray(searchState[key])
            ? (searchState[key] as Array<string>).map(i => ({ type: 'checkbox', value: i }))
            : { type: 'checkbox', value: searchState[key] };
        }
        return obj;
      }, {});

    if (item.isRefined) {
      setCheckedItems([...checkedItems.filter(i => i.label !== item.label)]);
    } else {
      setCheckedItems([...checkedItems.filter(i => i !== item), { ...item, isRefined: true }]);
    }

    if (isOnDisplayFacet) {
      const refinedCount = checkedItems.filter(i => i.isRefined).length;
      const hasStoreSelection = item.isRefined ? refinedCount - 1 > 0 : refinedCount + 1 > 0;
      trackStoreFilter({
        isSelected: hasStoreSelection,
        filterValue: hasStoreSelection ? 'Y' : 'N',
        filterType,
        searchType,
        searchTerm,
      });
    }

    if (Array.isArray(item.value) && item.value.length > 1) {
      if (item.isRefined && item.value.filter(i => i === item.label || i[0] === item.label).length > 0) {
        values = item.value.filter(i => i !== item.label && i[0] !== item.label);
      } else {
        values = item.value;
      }
    } else {
      values = item.value;
    }

    refine(values);
    updateSearchRefFromUrl(searchRef);

    window.dataLayer.push({
      event: 'custom.filter.set',
      group: 'site search',
      category: 'search',
      label: item.isRefined ? 'off' : 'on',
      name: facet,
      value: item.label,
      info: {
        element: e.target.name,
        position: trackingInfo.position,
        sortyBy: searchRef.current.sortBy || trackingInfo.defaults.sortBy,
        hitsPerPage: Number(searchRef.current.hitsPerPage) || trackingInfo.defaults.hitsPerPage,
      },
      newFilters: {
        ...filters,
        [facet.toLowerCase()]: values ? { type: 'checkbox', value: values } : undefined,
      },
      oldFilters: filters,
    });
  };

  const getTagLabel = (label: string) => storesData?.find(store => store.objectID === label)?.storeName ?? label;
  const getItemLabel = (label: string) =>
    storesData?.find(store => store.objectID === label)?.storeName ?? label + prependedLabel;

  return (
    <CheckboxFacetUI
      facet={facet}
      items={items}
      checkboxItems={checkboxItems}
      checkedItems={checkedItems}
      filterSearchTerm={filterSearchTerm}
      showCheckboxInput={showCheckboxInput}
      isSomeRefined={isSomeRefined}
      getTagLabel={getTagLabel}
      getItemLabel={getItemLabel}
      refineButtonRef={RefineButton}
      dropdownRef={RefineDropdown}
      onToggleDropdown={toggleDropdown.bind(this)}
      onFilterSearchChange={e => setFilterSearchTerm(e.target.value)}
      onToggleCheck={(e, item) => toggleCheck(e, item)}
      onLabelClick={labelOnClickHandler}
      onRemoveTag={(e, label) =>
        toggleCheck(
          e,
          checkboxItems.find(i => i.label === label)
        )
      }
    />
  );
};
